import constants from '../constants';
import { toAbsoluteUrl } from '../functions';
import type { APINewsItem, AuthorItem } from '../types';
import type {
    CollectionPageStructuredData,
    ItemListStructuredData,
    ListItemStructuredData,
    NewsArticleStructuredData,
    PersonStructuredData
} from './types';

const getPrimaryImageUrl = (imageSets: any[] | undefined): string | undefined => {
    const imageUrl = imageSets?.[0]?.originalUrl;

    return imageUrl ? toAbsoluteUrl(imageUrl) : undefined;
};

const getDateWithTimezone = (dateValue: string | undefined): string | undefined => {
    if (!dateValue) {
        return undefined;
    }

    if (/([+-]\d{2}:\d{2}|Z)$/i.test(dateValue)) {
        return dateValue;
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
        return undefined;
    }

    const pad = (value: number): string => value.toString().padStart(2, '0');
    const timezoneOffsetMinutes = -date.getTimezoneOffset();
    const sign = timezoneOffsetMinutes >= 0 ? '+' : '-';
    const absoluteOffsetMinutes = Math.abs(timezoneOffsetMinutes);
    const timezoneHours = Math.floor(absoluteOffsetMinutes / 60);
    const timezoneMinutes = absoluteOffsetMinutes % 60;

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${sign}${pad(timezoneHours)}:${pad(timezoneMinutes)}`;
};

const getAuthorStructuredData = (author: AuthorItem | null): PersonStructuredData | undefined => {
    if (!author?.name) {
        return undefined;
    }

    return {
        '@type': constants.structuredData.types.person,
        '@id': author.url ? toAbsoluteUrl(author.url) + constants.structuredData.ids.person : undefined,
        name: author.name,
        url: author.url ? toAbsoluteUrl(author.url) : undefined,
        image: getPrimaryImageUrl(author.previewImage),
        jobTitle: author.occupation || undefined,
    };
};

export const getNewsArticleStructuredData = (item: APINewsItem): NewsArticleStructuredData | null => {
    const detailUrl = toAbsoluteUrl(item.detailUrl);

    if (!detailUrl || !item.title) {
        return null;
    }

    return {
        '@type': constants.structuredData.types.newsArticle,
        '@id': detailUrl + constants.structuredData.ids.newsArticle,
        url: detailUrl,
        name: item.title,
        headline: item.title,
        description: item.intro || undefined,
        image: getPrimaryImageUrl(item.image),
        datePublished: getDateWithTimezone(item.date),
        author: getAuthorStructuredData(item.author),
        mainEntityOfPage: detailUrl,
    };
};

export const getCollectionPageStructuredData = (
    pageName: string,
    pageUrl: string,
    pageDescription?: string,
    items?: APINewsItem[]
): CollectionPageStructuredData => {
    const absolutePageUrl = toAbsoluteUrl(pageUrl);
    const itemListElements = (items ?? [])
        .map((item, index) => {
            const structuredDataItem = getNewsArticleStructuredData(item);

            if (!structuredDataItem) {
                return null;
            }

            const listItem: ListItemStructuredData = {
                '@type': constants.structuredData.types.listItem,
                position: index + 1,
                name: item.title,
                item: structuredDataItem,
            };

            return listItem;
        })
        .filter((item): item is ListItemStructuredData => item !== null);

    const itemList: ItemListStructuredData | undefined = itemListElements.length
        ? {
            '@type': constants.structuredData.types.itemList,
            '@id': absolutePageUrl + constants.structuredData.ids.itemList,
            itemListElement: itemListElements,
        }
        : undefined;

    return {
        '@context': constants.structuredData.values.context,
        '@type': constants.structuredData.types.collectionPage,
        '@id': absolutePageUrl + constants.structuredData.ids.collectionPage,
        url: absolutePageUrl,
        name: pageName,
        description: pageDescription || undefined,
        mainEntity: itemList,
    };
};

export const getItemListStructuredData = (
    itemListId: string,
    items?: APINewsItem[]
): ItemListStructuredData => {
    const itemListElements = (items ?? [])
        .map((item, index) => {
            const structuredDataItem = getNewsArticleStructuredData(item);

            if (!structuredDataItem) {
                return null;
            }

            const listItem: ListItemStructuredData = {
                '@type': constants.structuredData.types.listItem,
                position: index + 1,
                name: item.title,
                item: structuredDataItem,
            };

            return listItem;
        })
        .filter((item): item is ListItemStructuredData => item !== null);

    return {
        '@context': constants.structuredData.values.context,
        '@type': constants.structuredData.types.itemList,
        '@id': toAbsoluteUrl(itemListId),
        itemListElement: itemListElements,
    };
};

export const updateStructuredDataScript = (scriptId: string, structuredData: object, parentElement?: HTMLElement): void => {
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (!script && parentElement) {
        script = document.createElement('script');
        script.id = scriptId;
        parentElement.appendChild(script);
    }

    if (!script) return;

    script.setAttribute('type', constants.structuredData.scriptType);
    script.textContent = JSON.stringify(structuredData);
};
